import { useEffect, useState } from "react";
import { createSla, getSlaBreaches, listSlas } from "../api/support.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface SlaPolicy {
  id: string;
  name: string;
  priority: string;
  responseTargetMinutes: number;
  resolutionTargetMinutes: number;
  escalationTargetMinutes: number;
  isDefault: boolean;
}

interface BreachReport {
  on_track: number;
  warning: number;
  breached: number;
}

const PRIORITY_COLOR: Record<string, string> = {
  LOW: "text-gray-400",
  NORMAL: "text-blue-400",
  HIGH: "text-yellow-400",
  URGENT: "text-red-400",
};

function mins(m: number) {
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}d`;
}

export function SupportSlaPage() {
  const { token } = useAuth();
  const [policies, setPolicies] = useState<SlaPolicy[]>([]);
  const [breaches, setBreaches] = useState<BreachReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    priority: "NORMAL",
    responseTargetMinutes: "240",
    resolutionTargetMinutes: "1440",
    isDefault: false,
  });
  const [creating, setCreating] = useState(false);

  const reload = () => {
    void Promise.all([listSlas(token), getSlaBreaches(token)]).then(([p, b]) => {
      setPolicies((p as { items: SlaPolicy[] }).items ?? []);
      setBreaches(b as BreachReport);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    if (!form.name) return;
    setCreating(true);
    await createSla(token, {
      name: form.name,
      priority: form.priority,
      responseTargetMinutes: parseInt(form.responseTargetMinutes),
      resolutionTargetMinutes: parseInt(form.resolutionTargetMinutes),
      isDefault: form.isDefault,
    });
    setShowForm(false);
    setCreating(false);
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-4xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">SLA Dashboard</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500"
          >
            {showForm ? "Cancel" : "New SLA Policy"}
          </button>
        </div>
        {breaches && (
          <div className="mb-6 grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <p className="text-xs text-gray-400">On Track</p>
              <p className="text-2xl font-bold text-green-400">{breaches.on_track}</p>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <p className="text-xs text-gray-400">Warning</p>
              <p
                className={`text-2xl font-bold ${breaches.warning > 0 ? "text-yellow-400" : "text-gray-400"}`}
              >
                {breaches.warning}
              </p>
            </div>
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
              <p className="text-xs text-gray-400">Breached</p>
              <p
                className={`text-2xl font-bold ${breaches.breached > 0 ? "text-red-400" : "text-gray-400"}`}
              >
                {breaches.breached}
              </p>
            </div>
          </div>
        )}
        {showForm && (
          <div className="mb-6 space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Policy name"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <select
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-300"
              >
                {["LOW", "NORMAL", "HIGH", "URGENT"].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <div>
                <label className="mb-1 block text-xs text-gray-400">
                  Response Target (minutes)
                </label>
                <input
                  value={form.responseTargetMinutes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, responseTargetMinutes: e.target.value }))
                  }
                  type="number"
                  className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-400">
                  Resolution Target (minutes)
                </label>
                <input
                  value={form.resolutionTargetMinutes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, resolutionTargetMinutes: e.target.value }))
                  }
                  type="number"
                  className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
              />{" "}
              Default for this priority
            </label>
            <button
              onClick={() => void create()}
              disabled={creating}
              className="rounded bg-green-700 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create Policy"}
            </button>
          </div>
        )}
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-2">
            {policies.length === 0 && <p className="text-gray-400">No SLA policies defined.</p>}
            {policies.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-4 rounded-lg border border-gray-800 bg-gray-900 p-4"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white">{p.name}</span>
                    {p.isDefault && (
                      <span className="rounded bg-indigo-900/50 px-1.5 py-0.5 text-xs text-indigo-300">
                        default
                      </span>
                    )}
                  </div>
                  <span className={`text-xs ${PRIORITY_COLOR[p.priority] ?? "text-gray-400"}`}>
                    {p.priority} priority
                  </span>
                </div>
                <div className="text-right text-sm">
                  <p className="text-gray-300">
                    Response:{" "}
                    <span className="font-medium text-white">{mins(p.responseTargetMinutes)}</span>
                  </p>
                  <p className="text-gray-300">
                    Resolution:{" "}
                    <span className="font-medium text-white">
                      {mins(p.resolutionTargetMinutes)}
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
